import {
	Component,
	inject,
	OnDestroy,
	OnInit,
	Signal,
	signal,
	WritableSignal,
} from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { AuRightSidebarService } from '@core/services/au-right-sidebar.service';
import { filter, map, takeUntil } from 'rxjs/operators';

import { startCreateIncident } from '@app/pages/cmms/components/incident/state/incidents.actions';

import { IconSnackBarService } from '@app/core/services/icon-snack-bar.service';

import { CmmsLocationsSectionComponent } from '../../shared-form-group-controls/cmms-locations-section/cmms-locations-section.component';
import { CommonModule } from '@angular/common';
import { FileIncidentItemComponent } from '../file-incident-item/file-incident-item.component';
import { IResultData } from '@app/pages/cmms/interfaces/incident-detection.interfaces';
import {
	ICMMSLocation,
	LocationInput,
} from '@app/pages/cmms/interfaces/cmms-global.interfaces';
import { editingFile, selectDetectionTaskResult } from '../state/files.selectors';
import { Store } from '@ngrx/store';
import { updateDetectionTask } from '../state/files.action';
import { toSignal } from '@angular/core/rxjs-interop';
import { FilesService } from '@app/pages/cmms/services/files.service';
import { FormBuilder, FormControl } from '@angular/forms';
import { TranslateService } from '@tolgee/ngx';
import { formatCmmsLocationsToInput } from '@app/domain/locations/type-translations';

@Component({
	selector: 'file-incident-list',
	templateUrl: './file-incident-list.component.html',
	styleUrls: ['./file-incident-list.component.scss'],
	imports: [CommonModule, CmmsLocationsSectionComponent, FileIncidentItemComponent],
})
export class FileIncidentListComponent implements OnInit, OnDestroy {
	private readonly sidebarService = inject(AuRightSidebarService);
	private readonly notify = inject(IconSnackBarService);
	private readonly store$ = inject(Store);
	private readonly filesService = inject(FilesService);
	private readonly fb = inject(FormBuilder);
	private readonly translateService = inject(TranslateService);
	private destroy$: Subject<void> = new Subject<void>();
	fileEntity: Signal<{ pk: string; location: ICMMSLocation[] }> = toSignal(
		this.store$.select(editingFile).pipe(
			map(x => {
				const locations = x.locations ? x.locations : [];
				const updatedValues: any = locations.map(
					({ locationId, locationType, name }) => ({
						locationId,
						locationType,
						name,
					})
				);
				return { pk: x.pk, location: updatedValues };
			})
		)
	);

	locationValue: WritableSignal<LocationInput[]> = signal(
		formatCmmsLocationsToInput(this.fileEntity().location)
	);
	incidentTaskArray: Signal<IResultData[]> = toSignal(
		this.store$.select(selectDetectionTaskResult).pipe(
			takeUntil(this.destroy$),
			filter(incidentData => !!incidentData),
			filter(incidentData => this.fileEntity().pk in incidentData),
			map(incidentData => {
				const { deviation } = JSON.parse(incidentData[this.fileEntity().pk].results) as {
					deviation?: [IResultData];
				};
				return deviation;
			})
		)
	);
	incidentForm = this.fb.group({});
	isFullscreen$: Observable<boolean>;

	ngOnInit(): void {
		this.isFullscreen$ = this.sidebarService.sidebarFullscreen$;
	}

	changeStatus(incidentInfo: IResultData) {
		if (incidentInfo.status === 'CREATED') {
			this.startCreation(incidentInfo);
			return;
		} else if (incidentInfo.status === 'DISCARDED') {
			this.updateResults(incidentInfo.ID, { status: 'DISCARDED' });
			return;
		} else if (incidentInfo.status === 'DELETED') {
			if (incidentInfo.incident) {
				this.filesService.unlinkAttachedElements({
					pk: this.fileEntity().pk,
					incidents: incidentInfo.incident.pk,
				});
			}
			this.updateResults(incidentInfo.ID, { status: 'DELETED' });
		}
		this.updateResults(incidentInfo.ID, { status: null });
	}

	addChildControl(name: string, control: FormControl) {
		this.incidentForm.addControl(name, control);
		if (!this.locationValue().length) {
			this.incidentForm.get(name)?.setErrors({ required: true });
		}
	}

	startCreation(incidentTaskData: IResultData): void {
		if (!this.incidentForm.valid) {
			this.incidentForm.markAllAsTouched();
			this.notify.warning(this.translateService.instant('required_fields_warning'));
			return;
		}
		const location = this.locationValue();
		const formIncidentData = incidentTaskData;
		if (formIncidentData?.description) {
			const variables = {
				name: formIncidentData.name,
				locations: location,
				priority: parseInt('' + formIncidentData.priority),
				approvedBy: formIncidentData.approvedBy?.psId,
				assignee: formIncidentData.assignee?.psId,
				description: formIncidentData.description,
				category: formIncidentData.category,
			};
			this.store$.dispatch(
				startCreateIncident({
					variables,
					closeSidebar: false,
					dexterSource: {
						fileId: this.fileEntity().pk,
						result: this.incidentTaskArray(),
						resultId: formIncidentData.ID,
					},
				})
			);
		} else if (!formIncidentData.description) {
			this.notify.warning('Please fill the description field');
		}
	}
	updateResults(ID: string, change: Partial<Omit<IResultData, 'ID'>>) {
		this.store$.dispatch(
			updateDetectionTask({
				updateVariables: {
					fileId: this.fileEntity().pk,
					resultId: ID,
					result: this.incidentTaskArray(),
					updateValues: {
						...change,
					},
				},
			})
		);
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
